/* (#13) Telemetry / error reporting scaffold.

   Captures unhandled errors + promise rejections and POSTs them to
   /api/system/client-errors — the server-validated receiver
   (extra="forbid", message/stack/url/userAgent/kind, ring buffer 500).
   The retired /api/telemetry/error answers 410 since the 10.10 audit wave;
   a future revision can swap to Sentry / Posthog without changing call
   sites in the rest of the app.

   To attach Sentry SDK later:
     1. `npm install @sentry/react`
     2. Replace this module's `install()` body with `Sentry.init({...})`.
   The rest of the app keeps using `captureError()` and `captureEvent()`.
*/

declare const __HOLZCOM_PROD__: boolean;

/** Mirrors backend ClientErrorBody (routers/client_errors.py) — extra fields are forbidden. */
interface TelemetryError {
  message: string;
  stack?: string;
  url?: string;
  userAgent?: string;
  kind: 'error' | 'unhandledrejection';
}

const SAMPLE_RATE = 1.0; // upload every error in production; tune later

// Field caps mirror the backend model; overlong payloads would 422 and be lost.
const LIMITS = { message: 2000, stack: 4000, url: 500, userAgent: 300 } as const;

function send(err: TelemetryError): void {
  try {
    const body = JSON.stringify({
      message: err.message.slice(0, LIMITS.message),
      stack: err.stack ? err.stack.slice(0, LIMITS.stack) : undefined,
      url: err.url ? err.url.slice(0, LIMITS.url) : undefined,
      userAgent: err.userAgent ? err.userAgent.slice(0, LIMITS.userAgent) : undefined,
      kind: err.kind,
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon('/api/system/client-errors', new Blob([body], { type: 'application/json' }));
    } else {
      void fetch('/api/system/client-errors', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body }).catch(() => { /* VF-FE-15: same recursion guard as main.tsx */ });
    }
  } catch {
    // ignore — telemetry should never throw to user
  }
}

/**
 * (P10) Strip likely-PII from a string before exporting it.  Heuristics:
 *  - File system paths (/Users/…, /home/…, C:\…) → '/…/'
 *  - Long entity IDs (UUID-ish hex) → '<id>'
 *  - Query string values → keep param names only
 *  - File names with spaces or non-ASCII → '<file>'
 */
function sanitizeText(s: string | undefined): string | undefined {
  if (!s) return s;
  let out = s;
  // Public capability tokens live in the path, not only in query params.
  out = out.replace(/\/api\/public\/sketchup-(?:client|estimate)\/[^/?#\s]+\/[^/?#\s]+/gi,
    '/api/public/sketchup-<redacted>/<redacted>/<redacted>');
  out = out.replace(/\/(public\/share)\/[^/?#\s]+/gi, '/$1/<redacted>');
  out = out.replace(/\/client\/sketchup\/[^/?#\s]+\/[^/?#\s]+/gi, '/client/sketchup/<redacted>/<redacted>');
  out = out.replace(/([#&]share)=([^&\s]+)/gi, '$1=<redacted>');
  // File-system paths.
  out = out.replace(/\/Users\/[^/\s:]+/g, '/Users/<user>');
  out = out.replace(/\/home\/[^/\s:]+/g, '/home/<user>');
  out = out.replace(/[A-Z]:\\Users\\[^\\/\s:]+/g, 'C:\\Users\\<user>');
  // UUIDs first (must match before generic hex eats their tail group).
  out = out.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/g, '<uuid>');
  // Long hex IDs (10+ chars).  Not using \b — entity ids like
  // `module_a87488da7a_…` sit between underscores which are word chars.
  out = out.replace(/(^|[^a-f0-9])([a-f0-9]{10,})($|[^a-f0-9])/gi, '$1<id>$3');
  // Query-string values: ?model=foo → ?model=*
  out = out.replace(/(\?|&)([a-zA-Z0-9_]+)=([^&\s#]+)/g, '$1$2=*');
  return out;
}

function sanitizeUrl(url: string | undefined): string | undefined {
  if (!url) return url;
  try {
    const u = new URL(url, 'http://x');
    const sanitizedParams = Array.from(u.searchParams.keys()).map((k) => `${k}=*`).join('&');
    const pathname = u.pathname
      .replace(/\/api\/public\/sketchup-(?:client|estimate)\/[^/?#]+\/[^/?#]+/gi,
        '/api/public/sketchup-<redacted>/<redacted>/<redacted>')
      .replace(/\/(public\/share)\/[^/?#]+/gi, '/$1/<redacted>')
      .replace(/\/client\/sketchup\/[^/?#]+\/[^/?#]+/gi, '/client/sketchup/<redacted>/<redacted>');
    return `${u.origin}${pathname}${sanitizedParams ? '?' + sanitizedParams : ''}`;
  } catch {
    return sanitizeText(url);
  }
}

/**
 * (C4) Recursively sanitize a value — strings via sanitizeText, objects
 * walked, arrays mapped.  Numbers / booleans / null untouched.  Caps
 * recursion depth so a cyclic object can't lock the event loop.
 */
function sanitizeValue(v: unknown, depth = 0): unknown {
  if (depth > 6 || v == null) return v;
  if (typeof v === 'string') return sanitizeText(v);
  if (Array.isArray(v)) return v.slice(0, 50).map((x) => sanitizeValue(x, depth + 1));
  if (typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v)) {
      out[k] = sanitizeValue(val, depth + 1);
    }
    return out;
  }
  return v;
}

export function captureError(err: unknown, extra?: Record<string, unknown>): void {
  if (Math.random() > SAMPLE_RATE) return;
  const e = err instanceof Error ? err : new Error(String(err));
  const cleanExtra = (extra ? sanitizeValue(extra) : undefined) as Record<string, unknown> | undefined;
  // Location hints (line/col/ts) ride inside the stack — the validated
  // receiver takes exactly message/stack/url/userAgent/kind.
  const loc = [cleanExtra?.line, cleanExtra?.col].filter((v) => v !== undefined).join(':');
  const stackWithLoc = loc ? `${e.stack ?? e.message}\n    at <anonymous> (${loc})` : e.stack;
  const kind = cleanExtra?.kind === 'unhandledrejection' ? 'unhandledrejection' : 'error';
  send({
    message: sanitizeText(e.message) || '<empty>',
    stack: sanitizeText(stackWithLoc),
    url: sanitizeUrl(typeof location !== 'undefined' ? location.href : undefined),
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
    kind,
  });
}

export function installTelemetry(): void {
  if (typeof window === 'undefined') return;
  // Only auto-install in production by default; dev has the browser console
  // surface for debugging.
  if (typeof __HOLZCOM_PROD__ !== 'undefined' && !__HOLZCOM_PROD__) return;
  window.addEventListener('error', (ev) => {
    captureError(ev.error || ev.message, {
      line: ev.lineno,
      col: ev.colno,
      url: ev.filename,
    });
  });
  window.addEventListener('unhandledrejection', (ev) => {
    captureError((ev as PromiseRejectionEvent).reason, { kind: 'unhandledrejection' });
  });
}
